import { LegDetailsSubSection } from '@/ConsignmentDetails/LegsTab/types';
import { LegStatus } from '@api/Trinium/types';
import { BrokerCarrier } from '@api/Trinium/Endpoints/Broker/types';
import { Driver } from '@api/Trinium/Endpoints/Driver/types';
import { Leg } from '@api/Trinium/Endpoints/Leg/types';
import { DateRange, PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import { FilterOptionsState } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FieldProps, FormikProps, FormikValues } from 'formik';
import { ComponentType, FocusEventHandler, ReactNode } from 'react';
import { Consignment } from '@api/Trinium/Endpoints/Consignment/types';

/**
 *	Type definition of the props provided to the Sub-Form component rendered.
 */
export interface SubFormProps {
  name: string;
  parentFormProps: FormikProps<FormikValues>;
  subFormProps: FormikProps<FormikValues>;
  fieldProps: FormikFieldProps;
}

/**
 *	Type definition of the props available on the Formik <Field /> component.
 */
export type FormikFieldProps = FieldProps & {
  name: string;
  component: ComponentType<FieldProps>;
  label?: string;
  className?: string;

  /** Flag indicating to display the extended information of the sub-form if implemented */
  extended?: boolean;
  availabilityDatesOnly?: boolean;
  /** Bulk mode only - disable a date field when even one selected order's leg can't have that field set */
  bulkFullAvailableDisabled?: boolean;
  bulkEmptyAvailableDisabled?: boolean;
  bulkChassisAvailableDisabled?: boolean;

  // For Driver Form
  leg: Leg;
  showActions?: boolean;

  // For Appointment Form
  showLocationDetails?: boolean;
  usePickupNotes?: boolean;
  useDeliveryNotes?: boolean;

  readonly?: boolean;
};

/** CustomFieldProps for the Leg Formik Field */
export interface LegFormFieldProps {
  /** Index of the current leg form that we are sitting on */
  index: number;
  nextLegStatus: keyof typeof LegStatus;
  singleLegConsignment: boolean;
  /** Required for mobile Leg Item Action component */
  onLegSelect: (legId: number | undefined) => void;
  /** Provides ability to open to a specific section on the page */
  scrollTo?: LegDetailsSubSection;
  consignment: Consignment;
  consignmentIsWorkflow?: boolean;
}

/**
 *	Type definition of props provided to Formik <Field /> custom component rendered.
 *  Formik purposefully forwards on anything received by the component by using 'any' types,
 *  however we are using linting rules preventing the use of any, complicating this type.
 */
export interface CustomFieldProps {
  field: {
    id?: string;
    value: FormikValues;
    onChange: () => void;
    onBlur: () => void;
    name: string;
  };
  name: string;
  form: FormikProps<never>;
  label?: string;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom' | undefined;
  width?: string | number;
  height?: string | number;
  className?: string;
  rootClass?: string;
  variant?: 'outlined' | 'standard' | 'filled';
  required?: boolean;
  /** Callback method to do something else with the changed value */
  onChangeExtension?: (value?: unknown) => void;
  onBlurExtension?: (value?: unknown) => void;
  /** Callback method to do something additional when the input is cleared using the X icon */
  onClearExtension?: () => void;

  disabled?: boolean;
  disabledReason?: string;
  disabledTooltipClass?: string;

  //For Text Input
  startAdornmentIcon?: ReactNode;
  endAdornmentIcon?: ReactNode;
  placeholder?: string;
  textArea?: boolean;
  rows?: number;
  helperText?: string;

  // For Number Input
  max?: number;
  precision?: number;
  allowNegativeNumbers?: boolean;

  // For MultiSelectChipInput
  maxTags?: number;
  /** Optional cap on how many chips display before the (+N) indicator; by default as many as physically fit */
  displayTagsMax?: number;

  // For Dynamic Dropdown
  clearable?: boolean;

  // For Driver Dynamic Dropdown
  driver?: Driver | BrokerCarrier | null;

  // For DatePicker
  nowBtn?: boolean;

  // For Readonlyinput
  asPhoneLink?: boolean;

  /** For read-only input, used to format the dayjs object as text */
  dayjsFormat?: string;

  /** For Autocomplete */
  autoCompleteItems?: string[];
  isLoading?: boolean;
  freeSolo?: boolean;
  autoCompleteComplexItems?: unknown[];
  getOptionKey?: (option: unknown) => string;
  getOptionLabel?: (option: unknown) => string;
  getOptionDisabled?: (option: unknown) => boolean;
  isOptionEqualToValue?: (option: unknown, value: unknown) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: unknown;
    },
    value: unknown
  ) => ReactNode;
  /** Overrides MUI's option filtering, e.g. to cap the number of rendered options for large lists */
  filterOptions?: (options: unknown[], state: FilterOptionsState<unknown>) => unknown[];
  onInputChange?: (value: string) => void;
  popperClass?: string;

  /** For Driver Dropdown */
  showActions?: boolean;

  /** Consignment Modal Leg Form */
  legFormProps?: LegFormFieldProps;

  /** For Radio Button Group */
  radioItems?: string[];
  defaultRadioItem?: string;
}

/**
 *	Helper method for Formik Forms to set their field errors through deeply nested sub forms
 * @param field The Field that has an error
 * @param message The Error Message
 * @param func The Formik.setFieldError function
 */
export function setFormFieldError(field: string, message: string | undefined, func: (field: string, message: string | undefined) => void) {
  /** If the field string includes a subform, we need to handle differently */
  if (field.includes(':')) {
    const [subform, subfield] = field.split(':');
    func(subform, message ? `${subfield}:${message};` : undefined);
  } else {
    func(field, message);
  }
}

/**
 * Additional shared props that we are using across customized MUI inputs for the application.
 * Storing in a 'customProps' property to easily deconstruct from the MUI props allowing those to
 * be spread directly on the input and then consuming our customProps where needed.
 */
export interface AppInputCustomProps {
  customProps: {
    /** String of the input label */
    labelText: string;
    /** Flag indicating the input is dirty and should apply styles */
    isDirty?: boolean;
    /** Flag indicating the input is in an error state and should apply styles */
    isError?: boolean;
    /** When the input is in an error state, text to display in the tooltip */
    errorText?: string;
    /** Flag indicating that the input is required */
    required?: boolean;
    /** Function to execute when the clear button is clicked */
    onClear?: () => void;
    /** Function to execute on input blur */
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;

    /** If the input is in a disabled state, text to display in the tooltip */
    disabledReason?: string;
    /** Class applied to the Tooltip wrapper div when in a disabled state */
    disabledClass?: string;

    /** Beginning input adornment */
    startAdornment?: string | ReactNode;
    /** Ending input adornment */
    endAdornment?: string | ReactNode;
    /** Flag indicating the input is rendering inside of a table cell */
    enableCellStyles?: boolean;
    /** Additional CSS Class Name applied as specified by the component */
    extendedClassName?: string;

    /** Flag indicating, when true, that this is a multi line text box */
    textArea?: boolean;
    /** Optional rows value for textArea */
    rows?: number;
    /** Flag indicating, when true, that the "multiple" attribute true */
    multiple?: boolean;
    /** Flag to stop the event propagation on key down event. This is fixing a mobile issue on the data grid
     * where a text input is editable and certain keystrokes are used to navigate the grid.	 */
    stopPropagation?: boolean;

    /** Maximum length allowed on a text input */
    maxLength?: number;
    /** Maximum value allowed on a number input */
    maxValue?: number;
    /** Number of decimal places allowed. Default: 0 */
    precision?: number;
    /** Flag allowing the input to accept numbers below 0. Default: false */
    allowNegativeNumbers?: boolean;
    /** Flag to hide the clear icon adornment. Default: false */
    hideClearIcon?: boolean;

    /** Function to execute when the Now icon button is clicked for Date inputs */
    nowOnClick?: (now: Dayjs) => void;

    variant?: 'filled' | 'outlined' | 'standard';
  };
}

/**
 *	Mobile resolutions can support a subset of the full shortcuts available due to space
 */
export const mobileShortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('day'), today.endOf('day')];
    }
  },
  {
    label: 'Last Week',
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    }
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = dayjs();
      const endOfPrevMonth = today.startOf('month').subtract(1, 'day');
      return [endOfPrevMonth.startOf('month'), endOfPrevMonth];
    }
  }
];

/**
 *	Date Range shortcut presets that automatically populate the range picker
 */
export const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  ...mobileShortcutsItems,
  {
    label: 'Last Year',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('year').subtract(1, 'year'), today.startOf('year').subtract(1, 'day')];
    }
  },
  {
    label: 'Last 2 Years',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('year').subtract(2, 'year'), today.startOf('year').subtract(1, 'day')];
    }
  },
  {
    label: 'Year to Date',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('year'), today];
    }
  },
  { label: 'Reset', getValue: () => [dayjs().add(-1, 'year'), dayjs()] }
];
